import * as React from 'react';
import * as _ from 'lodash';

interface ColumnSizingInput {
  width: number;      // Initial pixel width
  minWidth?: number;  // Optional minimum pixel width
}

function getColumnKey(width: number, minWidth?: number): string {
  return minWidth !== undefined ? `${width}w-${minWidth}min` : `${width}w`;
}

export default function useScaledTableColumnWidths({
  columnWidths,
  containerRef,
  maxTableWidth,
  minTableWidth,
  additionalWidthRestrictions=0
}: {
  columnWidths: ColumnSizingInput[];
  containerRef?: React.RefObject<HTMLElement>;
  maxTableWidth?: number;
  minTableWidth?: number;
  additionalWidthRestrictions?: number; // can be used to account for padding, borders, anything that restricts the actual displayable width of the table
}): Record<string, string> {
  const [scaledWidths, setScaledWidths] = React.useState<Record<string, string>>(
    columnWidths.reduce((acc, { width, minWidth }) => {
      acc[getColumnKey(width, minWidth)] = `${width}px`;
      return acc;
    }, {} as Record<string, string>)
  );

  React.useEffect(() => {
    const updateWidths = () => {
      const containerWidth = (containerRef?.current?.offsetWidth || window.innerWidth) - additionalWidthRestrictions;
      const clampedWidth = Math.max(Math.min(containerWidth, maxTableWidth), minTableWidth);
      const scaleFactor = clampedWidth / maxTableWidth;

      const lockedWidths: Record<number, number> = {};
      const scalableColumns: { index: number; baseWidth: number }[] = [];

      columnWidths.forEach(({ width, minWidth }, index) => {
        const scaled = width * scaleFactor;
        const shouldLock = minWidth !== undefined && scaled <= minWidth;

        if (shouldLock) {
          lockedWidths[index] = minWidth!;
        } else {
          scalableColumns.push({ index, baseWidth: width });
        }
      });

      const lockedTotal = Object.values(lockedWidths).reduce((sum, w) => sum + w, 0);
      const availableSpace = clampedWidth - lockedTotal;
      const scalableBaseTotal = scalableColumns.reduce((sum, col) => sum + col.baseWidth, 0);

      const newWidths: Record<string, string> = {};

      columnWidths.forEach(({ width, minWidth }, index) => {
        const key = getColumnKey(width, minWidth);
        if (lockedWidths[index] !== undefined) {
          newWidths[key] = `${lockedWidths[index]}px`;
        } else {
          const proportion = width / scalableBaseTotal;
          const scaledWidth = availableSpace * proportion;
          const final = minWidth ? Math.max(scaledWidth, minWidth) : scaledWidth;
          newWidths[key] = `${final}px`;
        }
      });

      // Functional update against the latest state, with scaledWidths excluded from
      // the effect deps: the hook must never re-measure in response to its own
      // writes, or a container whose width tracks the table's content re-triggers
      // measurement in a feedback loop that shrinks the table until the tab locks up.
      setScaledWidths(prev => (_.isEqual(newWidths, prev) ? prev : newWidths));
    };

    updateWidths();
    // Debounced: recomputing on every resize event re-renders the whole table
    // per few px of drag, which locks up the tab on large tables. Waiting for
    // the resize to settle renders once instead.
    const debouncedUpdateWidths = _.debounce(updateWidths, 150);
    window.addEventListener('resize', debouncedUpdateWidths);
    return () => {
      debouncedUpdateWidths.cancel();
      window.removeEventListener('resize', debouncedUpdateWidths);
    };
  }, [columnWidths, containerRef?.current, maxTableWidth, minTableWidth]);

  return scaledWidths;
}
